
import {
  QuoteSubscriptionRequest,
  SubscriptionInfo,
  subscriptionType,
} from "functions/src/Subscriptions/SubscriptionsInterfaces";
import { UserSubscription } from "functions/src/Users/UsersInterfaces";
import {
  isAnytimeRenewableSubscriptionId,
  normalizePackageLabel,
} from "functions/src/Subscriptions/packageIds";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import {
  QuoteRequest,
  QuoteRequestType,
} from "functions/src/Payments/PaymentsInterfaces";
import { SubscriptionPackage } from "functions/src/Subscriptions/SubscriptionsInterfaces";
import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import PackageIcon from "../common/PackageIcon.vue";
import { PRODUCT_LICENSE } from "../common/productVisuals";

export default defineComponent({
  name: "subscription-status-card",
  components: { PackageIcon },
  data() {
    return {
      QuoteRequest: {} as QuoteRequest<QuoteRequestType>,
      editedEndDate: new Date().toISOString().substring(0, 10),
      manualDateUpdate:
        process.env.VUE_APP_MANUAL_SUBSCRIPTION_DATE_UPDATE === "true",
    };
  },
  props: {
    cardType: {
      type: Object as () => "license" | "package",
      required: true,
    },
    subscriptionId: { type: Object as () => subscriptionType, required: true },
    packageId: {
      type: String as () => SubscriptionPackage,
      required: true,
    },
    lastQuote: { type: Object, required: true },
    isQuoting: { type: Boolean, required: true },
  },
  emits: ["isActive", "clearQuote", "requestQuote"],
  computed: {
    ...mapGetters("auth", ["getUsernamePK", "getUseremail", "getUserPicture"]),
    ...mapGetters("subs", [
      "getSubscriptionConfig",
      "getUserSubscription",
      "durationUnitLocalized",
      "isUserActive",
    ]),
    ...mapGetters("sys", ["isOffline"]),
    isLoaded(): boolean {
      const loaded =
        this.SubscriptionConfig != undefined && this.UserStatus != undefined;
      return loaded;
    },
    /** El nivel traducido al vocabulario vivo, que es el que nombra el estilo. */
    levelClass(): string {
      return normalizePackageLabel(this.packageId) || "";
    },
    SubscriptionConfig(): SubscriptionInfo {
      return this.getSubscriptionConfig(this.subscriptionId);
    },
    SubscriptionDisplayName(): string {
      if (this.cardType == "license") {
        // Con `?.` y con salida: el documento del catálogo puede no haber
        // llegado aún por el listener. Sin él, la tarjeta revienta en render.
        return (
          this.getSubscriptionConfig(this.subscriptionId)?.displayName ??
          "Licencia"
        );
      }
      // AQUÍ NO HAY NADA QUE DISTINGUIR, y por eso no hay `switch`. Los tres
      // niveles vivos son HOMÓNIMOS de su documento de catálogo (`gtr-go`,
      // `gtr-fly`, `gtr-win`), así que los tres leen su nombre del mismo sitio:
      // el dato. Un `switch` con tres ramas que hacen lo mismo no separa
      // comportamientos, sólo enumera casos.
      //
      // Lo que había antes era una rama escrita a mano para el nivel de
      // academia, y existía por una razón que ya no se cumple: el nivel
      // retirado `gtr-basic` NO tenía documento con ese id —el suyo se llamaba
      // `trading-academy`— y `getSubscriptionConfig` no encontraba nada. Su
      // sucesor `gtr-fly` sí lo tiene. Con el literal fuera, cambiar el nombre
      // o el precio de un paquete es editar el catálogo: se refleja en el front
      // sin tocar código ni desplegar. Antes esta tarjeta se habría quedado con
      // el nombre viejo mientras las otras dos se actualizaban solas.
      //
      // El `??` cubre los dos casos legítimos sin documento: `none` (nunca
      // compró nada) y la ventana del runbook en la que el catálogo nuevo aún
      // no está sembrado. Sin él saldría un `undefined` en pantalla.
      const level = normalizePackageLabel(this.packageId);
      return this.getSubscriptionConfig(level)?.displayName ?? "Suscripción";
    },
    UserStatus(): UserSubscription {
      return this.getUserSubscription(this.subscriptionId);
    },
    PeriodsStep(): number {
      return this.SubscriptionConfig?.duration || 0;
    },
    DaysLeft(): number {
      // Calculated at subs store
      return this.UserStatus.daysLeft || 0;
    },
    daysLeftClass(): string {
      if (this.DaysLeft <= 5) {
        return "bg-danger";
      } else if (this.DaysLeft <= 10) {
        return "bg-warning";
      } else {
        return "bg-success";
      }
    },
    isActive(): boolean {
      const status =
        this.UserStatus.endDate != undefined &&
        this.UserStatus.startDate != undefined &&
        this.UserStatus.endDate > new Date();
      this.$emit("isActive", this.subscriptionId, status);
      return status;
    },
    Total(): string {
      const total = this.SubscriptionConfig.cost;
      return total.toLocaleString();
    },
    SubscriptionDurationUnitLocalized(): string {
      return this.durationUnitLocalized(
        this.SubscriptionConfig.duration,
        this.SubscriptionConfig?.durationUnit
      );
    },
    ExpirationDurationUnitLocalized(): string {
      return this.durationUnitLocalized(
        this.DaysLeft,
        this.SubscriptionConfig?.durationUnit
      );
    },
    CanRenewSubscription(): boolean {
      // La exención de renovación anticipada, en las DOS licencias que la
      // tienen: la de siempre y la GLOBAL (su copia). Es el espejo de
      // `PaymentsFunc` :135 — si aquí se ofreciera el botón y allí se rechazara
      // (o al revés) el usuario se quedaría mirando un error que no explica nada.
      if (isAnytimeRenewableSubscriptionId(this.subscriptionId)) {
        return this.isUserActive;
      } else {
        //Update days at function
        return this.DaysLeft < 3;
      }
    },
    /**
     * QUÉ PRODUCTO representa esta tarjeta, para el icono.
     *
     * No es lo mismo que `levelClass`: una tarjeta de licencia se pinta con la
     * clase del NIVEL del socio (para heredar el marco) pero el producto que
     * enseña es la LICENCIA, que tiene icono y color propios porque no es un
     * escalón de la escalera.
     */
    IconProduct(): string {
      return this.cardType === "license"
        ? PRODUCT_LICENSE
        : normalizePackageLabel(this.packageId) || "";
    },
  },
  methods: {
    async updateEndDate() {
      const newEndDate = new Date(this.editedEndDate);
      const updateSubscriptionEndDate = httpsCallable(
        functions,
        "Subscriptions-updateSubscriptionEndDate"
      );
      await updateSubscriptionEndDate({
        username: this.getUsernamePK,
        subscriptionId: this.subscriptionId,
        endDate: newEndDate,
      })
        .then(() => {
          alert("Fecha de vencimiento actualizada");
        })
        .catch((error) => {
          console.error(error);
          alert(
            "Error al actualizar la fecha de vencimiento: " + error.message
          );
        });
    },
    subscriptionQuote() {
      const fiveMinutes = new Date();
      fiveMinutes.setTime(fiveMinutes.getTime() + 5 * 60 * 1000); //5 minutes from now
      if (
        !(
          this.lastQuote.subscriptionId == this.subscriptionId &&
          this.lastQuote.username == this.getUsernamePK &&
          this.lastQuote.expirationTime > fiveMinutes
        )
      ) {
        this.$emit("clearQuote");
        const request: QuoteSubscriptionRequest = {
          subscriptionId: this.subscriptionId,
          duration: this.SubscriptionConfig.duration,
          durationUnit: this.SubscriptionConfig.durationUnit,
          username: this.getUsernamePK,
          email: this.getUseremail,
          isDirectAffiliate: false,
        };
        this.$emit("requestQuote", request);
      }
    },
  },
  mounted() {
    this.editedEndDate = (this.UserStatus.endDate as Date)
      ?.toISOString()
      .substring(0, 10);
  },
});
